import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faBuilding, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { useNavigate } from 'react-router-dom';
import { loadBusinessInfoSnapshot, formatDateTime } from './businessInfoShared';

function BusinessBankingDetails() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState(null);

  const loadPage = async () => {
    const snapshot = await loadBusinessInfoSnapshot(db);
    setBankAccounts(snapshot.bankAccounts);
    setPaymentSettings(snapshot.paymentDetails);
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load banking details:', error);
        setBankAccounts([]);
        setPaymentSettings(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const refreshPage = async () => {
    try {
      setRefreshing(true);
      await loadPage();
    } catch (error) {
      console.error('Failed to refresh banking details:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const summary = useMemo(() => ({
    bankCount: bankAccounts.length,
    accountCount: bankAccounts.filter((item) => item.accountNumber).length,
    namedCount: bankAccounts.filter((item) => item.accountName).length
  }), [bankAccounts]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>Banking Details</h4>
              <p className="appsSubtext">
                Current bank accounts used for collections, deposits, and any bank-transfer based payments.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Accounts</span>
                <strong>{summary.bankCount}</strong>
              </div>
              <div className="appsStatChip">
                <span>Named</span>
                <strong>{summary.namedCount}</strong>
              </div>
              <div className="appsStatChip">
                <span>Account Nos.</span>
                <strong>{summary.accountCount}</strong>
              </div>
            </div>
          </div>

          <div className="appsInsightsCard">
            <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
              <div>
                <p className="appsKicker">Source</p>
                <h6 className="mb-1">
                  <FontAwesomeIcon icon={faBuilding} className="me-2" />
                  Fee Payment Details
                </h6>
                <p className="appsSubtext">
                  These details are read from the same settings area used by finance operations.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <span className="cohortStatusBadge cohortStatusBadge--joined">
                  <FontAwesomeIcon icon={faCircleInfo} className="me-1" />
                  Updated {formatDateTime(paymentSettings?.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard">
            <Button variant="outline-dark" onClick={() => navigate('/settings')}>
              Open Settings
            </Button>
            <Button variant="outline-dark" onClick={refreshPage} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <div className="appsTableCard">
            <div className="appsTableHeader">
              <h6>Bank Accounts</h6>
              <span>{bankAccounts.length} saved</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status" />
                  <p>Loading banking details...</p>
                </div>
              ) : bankAccounts.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>No bank accounts saved yet</h6>
                  <p>Add them from Settings to show them here.</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Bank</th>
                      <th>Account Name</th>
                      <th>Account Number</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankAccounts.map((item) => (
                      <tr key={item.id}>
                        <td>{item.bankName || '—'}</td>
                        <td>{item.accountName || '—'}</td>
                        <td>{item.accountNumber || '—'}</td>
                        <td>{item.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessBankingDetails;
